import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { useParams } from "react-router-dom";
import { 
  Col, Row, Button, Form, FormGroup, Label, Input, 
  Modal, ModalHeader, ModalBody, ModalFooter,
  Card, CardBody, CardHeader, Badge, Table
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { v4 as uuidv4 } from "uuid";

function ProyectosSoporteCreate() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const isEditMode = !!id;
  
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const URL_SUPPORT_PROJECTS = process.env.REACT_APP_URL_SUPPORT_PROJECTS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;

  // Estados generales
  const [clientes, setClientes] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [slug, setSlug] = useState("");

  // Repositorios
  const [backRepoName, setBackRepoName] = useState("");
  const [backRepoFullName, setBackRepoFullName] = useState("");
  const [backRepoBranch, setBackRepoBranch] = useState("main");
  const [frontRepoName, setFrontRepoName] = useState("");
  const [frontRepoFullName, setFrontRepoFullName] = useState("");
  const [frontRepoBranch, setFrontRepoBranch] = useState("main");

  // Base de datos
  const [dbCluster, setDbCluster] = useState("");
  const [dbName, setDbName] = useState("");

  // Deploy
  const [backServiceUrl, setBackServiceUrl] = useState("");
  const [frontSiteUrl, setFrontSiteUrl] = useState("");

  // Grupos de WhatsApp
  const [whatsappGroups, setWhatsappGroups] = useState([]);
  const [modalGroup, setModalGroup] = useState(false);
  const [newGroupJid, setNewGroupJid] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupPurpose, setNewGroupPurpose] = useState("soporte");
  const [newGroupNumbers, setNewGroupNumbers] = useState("");

  // Configuración de IA
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiDelaySeconds, setAiDelaySeconds] = useState(30);
  const [aiOffHoursDelay, setAiOffHoursDelay] = useState(10);
  const [aiWorkHourStart, setAiWorkHourStart] = useState(8);
  const [aiWorkHourEnd, setAiWorkHourEnd] = useState(20);
  const [aiWorkDays, setAiWorkDays] = useState([1, 2, 3, 4, 5]);
  const [aiCanReadCode, setAiCanReadCode] = useState(true);
  const [aiCanCreatePR, setAiCanCreatePR] = useState(true);
  const [aiCanQueryDb, setAiCanQueryDb] = useState(true);
  const [aiCanFixData, setAiCanFixData] = useState(false);
  const [aiCanCheckLogs, setAiCanCheckLogs] = useState(true);
  const [aiAutoResolveMaxRisk, setAiAutoResolveMaxRisk] = useState("low");
  const [aiToneNotes, setAiToneNotes] = useState("");
  const [aiModel, setAiModel] = useState("claude-sonnet-4-5-20250514");
  const [aiMaxTokens, setAiMaxTokens] = useState(500);

  // Stack Técnico
  const [stackBackRuntime, setStackBackRuntime] = useState("Node.js");
  const [stackBackFramework, setStackBackFramework] = useState("Express");
  const [stackBackOrm, setStackBackOrm] = useState("Mongoose");
  const [stackFrontFramework, setStackFrontFramework] = useState("");
  const [stackFrontStyling, setStackFrontStyling] = useState("");
  const [stackFrontState, setStackFrontState] = useState("");
  const [stackDatabase, setStackDatabase] = useState("MongoDB");
  const [stackFileStorage, setStackFileStorage] = useState("");

  // Notas
  const [notes, setNotes] = useState("");
  const [validaBoton, setValidaBoton] = useState(true);

  useEffect(() => {
    // Cargamos clientes primero; en modo edición cargamos el proyecto
    // dentro del callback para garantizar que las opciones del select
    // ya existen cuando se setea clientId (evita el bug del select vacío).
    getClientes(() => {
      if (isEditMode) getProyecto();
    });
  }, []);

  const getClientes = (callback) => {
    axios
      .get(URL_CLIENTES, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setClientes(response.data);
        if (callback) callback();
      })
      .catch((err) => console.log(err));
  };

  const getProyecto = () => {
    axios
      .get(`${URL_SUPPORT_PROJECTS}/${id}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const proyecto = response.data;
        // clientId viene populado (objeto) o como string — extraer siempre el _id
        const cId = proyecto.clientId;
        setClientId(cId && typeof cId === 'object' ? cId._id?.toString() || "" : cId?.toString() || "");
        setClientName(proyecto.clientName || "");
        setSlug(proyecto.slug || "");
        
        // Repos
        setBackRepoName(proyecto.repos?.back?.name || "");
        setBackRepoFullName(proyecto.repos?.back?.fullName || "");
        setBackRepoBranch(proyecto.repos?.back?.branch || "main");
        setFrontRepoName(proyecto.repos?.front?.name || "");
        setFrontRepoFullName(proyecto.repos?.front?.fullName || "");
        setFrontRepoBranch(proyecto.repos?.front?.branch || "main");
        
        // Database
        setDbCluster(proyecto.database?.cluster || "");
        setDbName(proyecto.database?.dbName || "");
        
        // Deploy
        setBackServiceUrl(proyecto.deploy?.back?.serviceUrl || "");
        setFrontSiteUrl(proyecto.deploy?.front?.siteUrl || "");
        
        // WhatsApp Groups
        setWhatsappGroups(proyecto.whatsappGroups || []);
        
        // AI Config
        setAiEnabled(proyecto.aiConfig?.enabled || false);
        setAiDelaySeconds(proyecto.aiConfig?.delaySeconds || 30);
        setAiOffHoursDelay(proyecto.aiConfig?.offHoursDelaySeconds || 10);
        setAiWorkHourStart(proyecto.aiConfig?.workHours?.start || 8);
        setAiWorkHourEnd(proyecto.aiConfig?.workHours?.end || 20);
        setAiWorkDays(proyecto.aiConfig?.workHours?.workDays || [1, 2, 3, 4, 5]);
        setAiCanReadCode(proyecto.aiConfig?.permissions?.canReadCode ?? true);
        setAiCanCreatePR(proyecto.aiConfig?.permissions?.canCreatePR ?? true);
        setAiCanQueryDb(proyecto.aiConfig?.permissions?.canQueryDb ?? true);
        setAiCanFixData(proyecto.aiConfig?.permissions?.canFixData ?? false);
        setAiCanCheckLogs(proyecto.aiConfig?.permissions?.canCheckLogs ?? true);
        setAiAutoResolveMaxRisk(proyecto.aiConfig?.permissions?.autoResolveMaxRisk || "low");
        setAiToneNotes(proyecto.aiConfig?.toneNotes || "");
        setAiModel(proyecto.aiConfig?.model || "claude-sonnet-4-5-20250514");
        setAiMaxTokens(proyecto.aiConfig?.maxTokensResponse || 500);
        
        // Stack
        setStackBackRuntime(proyecto.stack?.back?.runtime || "Node.js");
        setStackBackFramework(proyecto.stack?.back?.framework || "Express");
        setStackBackOrm(proyecto.stack?.back?.orm || "Mongoose");
        setStackFrontFramework(proyecto.stack?.front?.framework || "");
        setStackFrontStyling(proyecto.stack?.front?.styling || "");
        setStackFrontState(proyecto.stack?.front?.stateManagement || "");
        setStackDatabase(proyecto.stack?.database || "MongoDB");
        setStackFileStorage(proyecto.stack?.fileStorage || "");
        
        // Notes
        setNotes(proyecto.notes || "");
      })
      .catch((err) => console.log(err));
  };

  const handleClientChange = (clienteId) => {
    const cliente = clientes.find(c => c._id === clienteId);
    if (cliente) {
      setClientId(clienteId);
      setClientName(cliente.nombre_comercial);
      setSlug(cliente.nombre_comercial.toLowerCase().replace(/\s+/g, '-'));
    }
  };

  const toggleWorkDay = (day) => {
    if (aiWorkDays.includes(day)) {
      setAiWorkDays(aiWorkDays.filter(d => d !== day));
    } else {
      setAiWorkDays([...aiWorkDays, day].sort());
    }
  };

  const addWhatsappGroup = () => {
    const newGroup = {
      groupJid: newGroupJid,
      groupName: newGroupName,
      purpose: newGroupPurpose,
      clientNumbers: newGroupNumbers.split(',').map(n => n.trim()).filter(n => n),
    };
    
    if (isEditMode) {
      axios
        .post(
          `${URL_SUPPORT_PROJECTS}Group/${id}`,
          newGroup,
          {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }
        )
        .then((response) => {
          Swal.fire("Éxito", "Grupo agregado correctamente", "success");
          setWhatsappGroups([...whatsappGroups, newGroup]);
          setModalGroup(false);
          resetGroupModal();
        })
        .catch((err) => {
          console.log(err);
          Swal.fire("Error", "No se pudo agregar el grupo", "error");
        });
    } else {
      setWhatsappGroups([...whatsappGroups, newGroup]);
      setModalGroup(false);
      resetGroupModal();
    }
  };

  const removeWhatsappGroup = (groupJid) => {
    if (isEditMode) {
      axios
        .delete(`${URL_SUPPORT_PROJECTS}Group/${id}/${groupJid}`, {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        })
        .then((response) => {
          Swal.fire("Éxito", "Grupo eliminado correctamente", "success");
          setWhatsappGroups(whatsappGroups.filter(g => g.groupJid !== groupJid));
        })
        .catch((err) => {
          console.log(err);
          Swal.fire("Error", "No se pudo eliminar el grupo", "error");
        });
    } else {
      setWhatsappGroups(whatsappGroups.filter(g => g.groupJid !== groupJid));
    }
  };

  const resetGroupModal = () => {
    setNewGroupJid("");
    setNewGroupName("");
    setNewGroupPurpose("soporte");
    setNewGroupNumbers("");
  };

  const saveProyecto = async (event) => {
    event.preventDefault();
    setValidaBoton(false);

    const proyectoData = {
      clientId,
      clientName,
      slug,
      repos: {
        back: {
          name: backRepoName,
          fullName: backRepoFullName,
          branch: backRepoBranch,
        },
        front: {
          name: frontRepoName,
          fullName: frontRepoFullName,
          branch: frontRepoBranch,
        },
      },
      database: {
        cluster: dbCluster,
        dbName,
      },
      deploy: {
        back: {
          platform: "railway",
          serviceUrl: backServiceUrl,
        },
        front: {
          platform: "netlify",
          siteUrl: frontSiteUrl,
        },
      },
      whatsappGroups,
      aiConfig: {
        enabled: aiEnabled,
        delaySeconds: aiDelaySeconds,
        offHoursDelaySeconds: aiOffHoursDelay,
        workHours: {
          start: aiWorkHourStart,
          end: aiWorkHourEnd,
          timezone: "America/Mexico_City",
          workDays: aiWorkDays,
        },
        permissions: {
          canReadCode: aiCanReadCode,
          canCreatePR: aiCanCreatePR,
          canQueryDb: aiCanQueryDb,
          canFixData: aiCanFixData,
          canCheckLogs: aiCanCheckLogs,
          autoResolveMaxRisk: aiAutoResolveMaxRisk,
        },
        toneNotes: aiToneNotes,
        model: aiModel,
        maxTokensResponse: aiMaxTokens,
      },
      stack: {
        back: {
          runtime: stackBackRuntime,
          framework: stackBackFramework,
          orm: stackBackOrm,
        },
        front: {
          framework: stackFrontFramework,
          styling: stackFrontStyling,
          stateManagement: stackFrontState,
        },
        database: stackDatabase,
        fileStorage: stackFileStorage,
      },
      notes,
    };

    try {
      if (isEditMode) {
        await axios.patch(
          `${URL_SUPPORT_PROJECTS}/${id}`,
          proyectoData,
          {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }
        );
        Swal.fire("Éxito", "Proyecto actualizado correctamente", "success");
      } else {
        await axios.post(
          URL_SUPPORT_PROJECTS,
          proyectoData,
          {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }
        );
        Swal.fire("Éxito", "Proyecto creado correctamente", "success");
      }
      
      setTimeout(() => {
        window.location.href = "/ListadoProyectosSoporte";
      }, 1500);
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "No se pudo guardar el proyecto", "error");
      setValidaBoton(true);
    }
  };

  return (
    <>
      <Header />
      {user.menu_soporte ? (
        <div className="container-fluid">
          <br />
          <Card>
            <CardHeader>
              <h3>{isEditMode ? "Editar" : "Crear"} Proyecto de Soporte</h3>
            </CardHeader>
            <CardBody>
              <Form onSubmit={saveProyecto}>
                {/* Información General */}
                <h5 className="text-primary">Información General</h5>
                <hr />
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Cliente *</Label>
                      <Input
                        type="select"
                        value={clientId}
                        onChange={(e) => handleClientChange(e.target.value)}
                        required
                      >
                        <option value="">Seleccione un cliente...</option>
                        {clientes.map((cliente) => (
                          <option key={cliente._id} value={cliente._id}>
                            {cliente.nombre_comercial}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Nombre del Proyecto *</Label>
                      <Input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Slug (identificador único) *</Label>
                      <Input
                        type="text"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        placeholder="ejemplo: partners, cocabella"
                        required
                      />
                      <small className="text-muted">
                        Usar minúsculas y guiones, ej: partners, coca-bella
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Repositorios */}
                <br />
                <h5 className="text-primary">Repositorios GitHub</h5>
                <hr />
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Backend - Nombre del Repo</Label>
                      <Input
                        type="text"
                        value={backRepoName}
                        onChange={(e) => setBackRepoName(e.target.value)}
                        placeholder="PartnersBack"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Backend - Full Name</Label>
                      <Input
                        type="text"
                        value={backRepoFullName}
                        onChange={(e) => setBackRepoFullName(e.target.value)}
                        placeholder="sistemify/PartnersBack"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Branch</Label>
                      <Input
                        type="text"
                        value={backRepoBranch}
                        onChange={(e) => setBackRepoBranch(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Frontend - Nombre del Repo</Label>
                      <Input
                        type="text"
                        value={frontRepoName}
                        onChange={(e) => setFrontRepoName(e.target.value)}
                        placeholder="PartnersFront"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Frontend - Full Name</Label>
                      <Input
                        type="text"
                        value={frontRepoFullName}
                        onChange={(e) => setFrontRepoFullName(e.target.value)}
                        placeholder="sistemify/PartnersFront"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Branch</Label>
                      <Input
                        type="text"
                        value={frontRepoBranch}
                        onChange={(e) => setFrontRepoBranch(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Base de Datos */}
                <br />
                <h5 className="text-primary">Base de Datos</h5>
                <hr />
                <Row>
                  <Col md={8}>
                    <FormGroup>
                      <Label>Cluster de MongoDB *</Label>
                      <Input
                        type="text"
                        value={dbCluster}
                        onChange={(e) => setDbCluster(e.target.value)}
                        placeholder="sistemify5"
                        required
                      />
                      <small className="text-muted">Solo el nombre del cluster. Las credenciales no se guardan aquí: viven en variables de entorno.</small>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Nombre de la Base de Datos *</Label>
                      <Input
                        type="text"
                        value={dbName}
                        onChange={(e) => setDbName(e.target.value)}
                        placeholder="partners"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Deploy */}
                <br />
                <h5 className="text-primary">Deploy</h5>
                <hr />
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Backend URL (Railway)</Label>
                      <Input
                        type="url"
                        value={backServiceUrl}
                        onChange={(e) => setBackServiceUrl(e.target.value)}
                        placeholder="https://partnersback.up.railway.app"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Frontend URL (Netlify)</Label>
                      <Input
                        type="url"
                        value={frontSiteUrl}
                        onChange={(e) => setFrontSiteUrl(e.target.value)}
                        placeholder="https://partners.netlify.app"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Grupos de WhatsApp */}
                <br />
                <h5 className="text-primary">Grupos de WhatsApp</h5>
                <hr />
                <Row>
                  <Col md={12}>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => setModalGroup(true)}
                      type="button"
                    >
                      <i className="fas fa-plus"></i> Agregar Grupo
                    </Button>
                    <br /><br />
                    {whatsappGroups.length > 0 ? (
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>JID</th>
                            <th>Propósito</th>
                            <th>Números</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {whatsappGroups.map((group, index) => (
                            <tr key={index}>
                              <td>{group.groupName}</td>
                              <td><small>{group.groupJid}</small></td>
                              <td>
                                <Badge color={
                                  group.purpose === 'soporte' ? 'success' :
                                  group.purpose === 'general' ? 'info' :
                                  group.purpose === 'pagos' ? 'warning' : 'secondary'
                                }>
                                  {group.purpose}
                                </Badge>
                              </td>
                              <td>
                                <small>{group.clientNumbers?.join(', ') || 'N/A'}</small>
                              </td>
                              <td>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeWhatsappGroup(group.groupJid)}
                                  type="button"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted">No hay grupos agregados</p>
                    )}
                  </Col>
                </Row>

                {/* Configuración de IA */}
                <br />
                <h5 className="text-primary">Configuración de IA</h5>
                <hr />
                <Row>
                  <Col md={12}>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiEnabled}
                          onChange={(e) => setAiEnabled(e.target.checked)}
                        />{" "}
                        IA Habilitada
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                <br />
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Delay en horario laboral (segundos)</Label>
                      <Input
                        type="number"
                        value={aiDelaySeconds}
                        onChange={(e) => setAiDelaySeconds(parseInt(e.target.value))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Delay fuera de horario (segundos)</Label>
                      <Input
                        type="number"
                        value={aiOffHoursDelay}
                        onChange={(e) => setAiOffHoursDelay(parseInt(e.target.value))}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Hora Inicio</Label>
                      <Input
                        type="select"
                        value={aiWorkHourStart}
                        onChange={(e) => setAiWorkHourStart(parseInt(e.target.value))}
                      >
                        {Array.from({length: 24}, (_, i) => (
                          <option key={i} value={i}>{i}:00</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Hora Fin</Label>
                      <Input
                        type="select"
                        value={aiWorkHourEnd}
                        onChange={(e) => setAiWorkHourEnd(parseInt(e.target.value))}
                      >
                        {Array.from({length: 24}, (_, i) => (
                          <option key={i} value={i}>{i}:00</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Días Laborales</Label>
                      <div>
                        {[
                          {day: 1, label: 'Lun'},
                          {day: 2, label: 'Mar'},
                          {day: 3, label: 'Mié'},
                          {day: 4, label: 'Jue'},
                          {day: 5, label: 'Vie'},
                          {day: 6, label: 'Sáb'},
                          {day: 0, label: 'Dom'},
                        ].map(({day, label}) => (
                          <FormGroup check inline key={day}>
                            <Label check>
                              <Input
                                type="checkbox"
                                checked={aiWorkDays.includes(day)}
                                onChange={() => toggleWorkDay(day)}
                              />{" "}
                              {label}
                            </Label>
                          </FormGroup>
                        ))}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <br />
                <h6>Permisos de IA</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiCanReadCode}
                          onChange={(e) => setAiCanReadCode(e.target.checked)}
                        />{" "}
                        Leer código de los repositorios
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiCanCreatePR}
                          onChange={(e) => setAiCanCreatePR(e.target.checked)}
                        />{" "}
                        Crear Pull Requests
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiCanQueryDb}
                          onChange={(e) => setAiCanQueryDb(e.target.checked)}
                        />{" "}
                        Consultar base de datos del cliente
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiCanFixData}
                          onChange={(e) => setAiCanFixData(e.target.checked)}
                        />{" "}
                        Modificar base de datos del cliente (⚠️ Peligroso)
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={aiCanCheckLogs}
                          onChange={(e) => setAiCanCheckLogs(e.target.checked)}
                        />{" "}
                        Ver logs de Railway
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                <br />
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Nivel máximo de auto-resolución</Label>
                      <Input
                        type="select"
                        value={aiAutoResolveMaxRisk}
                        onChange={(e) => setAiAutoResolveMaxRisk(e.target.value)}
                      >
                        <option value="low">Bajo</option>
                        <option value="medium">Medio</option>
                        <option value="high">Alto</option>
                      </Input>
                      <small className="text-muted">
                        Qué nivel de riesgo puede resolver la IA sin intervención humana
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Modelo de IA</Label>
                      <Input
                        type="text"
                        value={aiModel}
                        onChange={(e) => setAiModel(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Notas sobre el tono de respuesta</Label>
                      <Input
                        type="textarea"
                        rows="3"
                        value={aiToneNotes}
                        onChange={(e) => setAiToneNotes(e.target.value)}
                        placeholder="Ej: Ser muy formal, usar lenguaje técnico, ser amigable..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Stack Técnico */}
                <br />
                <h5 className="text-primary">Stack Técnico</h5>
                <hr />
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Backend - Runtime</Label>
                      <Input
                        type="text"
                        value={stackBackRuntime}
                        onChange={(e) => setStackBackRuntime(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Backend - Framework</Label>
                      <Input
                        type="text"
                        value={stackBackFramework}
                        onChange={(e) => setStackBackFramework(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Backend - ORM</Label>
                      <Input
                        type="text"
                        value={stackBackOrm}
                        onChange={(e) => setStackBackOrm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Frontend - Framework</Label>
                      <Input
                        type="text"
                        value={stackFrontFramework}
                        onChange={(e) => setStackFrontFramework(e.target.value)}
                        placeholder="React, Next.js, Vue..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Frontend - Estilos</Label>
                      <Input
                        type="text"
                        value={stackFrontStyling}
                        onChange={(e) => setStackFrontStyling(e.target.value)}
                        placeholder="Tailwind, Bootstrap, CSS Modules..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Frontend - State Management</Label>
                      <Input
                        type="text"
                        value={stackFrontState}
                        onChange={(e) => setStackFrontState(e.target.value)}
                        placeholder="Context, Redux, Zustand..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Almacenamiento de Archivos</Label>
                      <Input
                        type="text"
                        value={stackFileStorage}
                        onChange={(e) => setStackFileStorage(e.target.value)}
                        placeholder="S3, Cloudinary, Firebase..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Base de Datos</Label>
                      <Input
                        type="text"
                        value={stackDatabase}
                        onChange={(e) => setStackDatabase(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Notas */}
                <br />
                <h5 className="text-primary">Notas</h5>
                <hr />
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Notas Generales</Label>
                      <Input
                        type="textarea"
                        rows="4"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Observaciones generales sobre el proyecto..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Botones */}
                <br />
                <Row>
                  <Col md={12}>
                    <Button color="success" type="submit" disabled={!validaBoton}>
                      <i className="fas fa-save"></i>{" "}
                      {isEditMode ? "Actualizar" : "Guardar"} Proyecto
                    </Button>{" "}
                    <Button color="secondary" href="/ListadoProyectosSoporte">
                      <i className="fas fa-times"></i> Cancelar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </div>
      ) : undefined}

      {/* Modal para agregar grupo de WhatsApp */}
      <Modal isOpen={modalGroup} toggle={() => setModalGroup(!modalGroup)}>
        <ModalHeader toggle={() => setModalGroup(!modalGroup)}>
          Agregar Grupo de WhatsApp
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Group JID *</Label>
            <Input
              type="text"
              value={newGroupJid}
              onChange={(e) => setNewGroupJid(e.target.value)}
              placeholder="120363123456789@g.us"
            />
            <small className="text-muted">
              ID del grupo en formato de WhatsApp/Baileys
            </small>
          </FormGroup>
          <FormGroup>
            <Label>Nombre del Grupo</Label>
            <Input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Soporte Partners"
            />
          </FormGroup>
          <FormGroup>
            <Label>Propósito</Label>
            <Input
              type="select"
              value={newGroupPurpose}
              onChange={(e) => setNewGroupPurpose(e.target.value)}
            >
              <option value="soporte">Soporte</option>
              <option value="general">General</option>
              <option value="pagos">Pagos</option>
              <option value="otro">Otro</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Números del Cliente (separados por coma)</Label>
            <Input
              type="text"
              value={newGroupNumbers}
              onChange={(e) => setNewGroupNumbers(e.target.value)}
              placeholder="5215512345678, 5215587654321"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={addWhatsappGroup}>
            Agregar
          </Button>{" "}
          <Button color="secondary" onClick={() => setModalGroup(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ProyectosSoporteCreate;
